// src/main.tsx
// Build: 2026-01-02-v2 - tenantRewardModal fix
import { initSentry, Sentry } from "./lib/sentry";

// Sentryを最初に初期化（他のインポートより前）
initSentry();

import React, { lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { Buffer } from "buffer";
import { PrivyProvider } from "@privy-io/react-auth";
import { buildPrivyConfig } from "./config/privyConfig";
import { ThirdwebProvider } from "@thirdweb-dev/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { initGifterraStatusBar } from "./utils/statusBar";
import { initGifterraKeyboard } from "./utils/keyboard";

// =============================
// コード分割: React.lazy によるルートベース遅延読み込み
// =============================
const ReceivePage = lazy(() => import("./pages/ReceivePage").then(m => ({ default: m.ReceivePage })));
const MypagePage = lazy(() => import("./pages/Mypage").then(m => ({ default: m.MypagePage })));
const LoginPage = lazy(() => import("./pages/Login").then(m => ({ default: m.LoginPage })));
const RewardApp = lazy(() => import("./reward-ui/App"));
const VendingApp = lazy(() => import("./vending-ui/App"));
const AdminDashboard = lazy(() => import("./admin/Dashboard"));
const SuperAdminPage = lazy(() => import("./pages/SuperAdmin").then(m => ({ default: m.SuperAdminPage })));
const TenantProvider = lazy(() => import("./admin/contexts/TenantContext").then(m => ({ default: m.TenantProvider })));
const PaymentTerminal = lazy(() => import("./admin/components/PaymentTerminal").then(m => ({ default: m.PaymentTerminal })));
const PaymentTerminalMobile = lazy(() => import("./admin/components/PaymentTerminalMobile").then(m => ({ default: m.PaymentTerminalMobile })));
const AppWrapper = lazy(() => import("./components/AppWrapper").then(m => ({ default: m.AppWrapper })));
import { ErrorBoundary } from "./components/ErrorBoundary";
const TermsOfServicePage = lazy(() => import("./pages/TermsOfService").then(m => ({ default: m.TermsOfServicePage })));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicy").then(m => ({ default: m.PrivacyPolicyPage })));
const ProfilePage = lazy(() => import("./pages/ProfilePage").then(m => ({ default: m.ProfilePage })));
const UserProfile = lazy(() => import("./pages/UserProfile").then(m => ({ default: m.UserProfile })));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage").then(m => ({ default: m.NotificationsPage })));
const TenantIntroduction = lazy(() => import("./pages/TenantIntroduction").then(m => ({ default: m.TenantIntroduction })));
const PlanSelection = lazy(() => import("./pages/PlanSelection").then(m => ({ default: m.PlanSelection })));
const TenantPayment = lazy(() => import("./pages/TenantPayment").then(m => ({ default: m.TenantPayment })));
const TenantPaymentSuccess = lazy(() => import("./pages/TenantPaymentSuccess").then(m => ({ default: m.TenantPaymentSuccess })));
const TenantApplication = lazy(() => import("./pages/TenantApplication").then(m => ({ default: m.TenantApplication })));
const TipOverlayPage = lazy(() => import("./pages/TipOverlayPage"));
const TipPage = lazy(() => import("./pages/TipPage").then(m => ({ default: m.TipPage })));
const CheckinPage = lazy(() => import("./pages/CheckinPage"));
const StampCheckinPage = lazy(() => import("./pages/StampCheckin").then(m => ({ default: m.StampCheckinPage })));
const StampRallyBookPage = lazy(() => import("./pages/StampRallyBook").then(m => ({ default: m.StampRallyBookPage })));
const FreeMintPage = lazy(() => import("./pages/FreeMintPage").then(m => ({ default: m.FreeMintPage })));
const EffectTestPage = lazy(() => import("./pages/EffectTestPage").then(m => ({ default: m.EffectTestPage })));
const MyPurchasesPage = lazy(() => import("./pages/MyPurchasesPage").then(m => ({ default: m.MyPurchasesPage })));
const RelayPayPage = lazy(() => import("./pages/RelayPayPage").then(m => ({ default: m.RelayPayPage })));
const CommunityListPage = lazy(() => import("./pages/CommunityListPage").then(m => ({ default: m.CommunityListPage })));
const CommunityDetailPage = lazy(() => import("./pages/CommunityDetailPage").then(m => ({ default: m.CommunityDetailPage })));
const CommunityCreatePage = lazy(() => import("./pages/CommunityCreatePage").then(m => ({ default: m.CommunityCreatePage })));
const CommunityInvitePage = lazy(() => import("./pages/CommunityInvitePage").then(m => ({ default: m.CommunityInvitePage })));
const CommunitySettingsPage = lazy(() => import("./pages/CommunitySettingsPage").then(m => ({ default: m.CommunitySettingsPage })));

// ローディングコンポーネント
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #018a9a 0%, #01525e 100%)',
    color: 'white',
    fontSize: '18px',
    fontWeight: 600,
  }}>
    読み込み中...
  </div>
);

// Polyfill Buffer for browser environment (required for Web3 libraries)
window.Buffer = window.Buffer || Buffer;

// =============================
// Service Worker 登録（PWA対応）
// ⚠️ 既存のService Workerを強制アンインストールして新バージョンを適用
// =============================
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    try {
      // 既存のService Workerをすべてアンインストール
      const registrations = await navigator.serviceWorker.getRegistrations();

      let needsReload = false;

      for (const registration of registrations) {
        const unregistered = await registration.unregister();
        if (unregistered) {
          needsReload = true;
        }
      }

      // キャッシュもすべて削除
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        if (cacheNames.length > 0) {
          needsReload = true;
        }
      }

      // 古いService Workerがあった場合、リロードして完全にクリア
      if (needsReload && !sessionStorage.getItem('sw-cleaned')) {
        sessionStorage.setItem('sw-cleaned', 'true');
        window.location.reload();
        return;
      }

      // 新しいService Workerを登録
      const registration = await navigator.serviceWorker.register('/sw.js');

      // 新しいバージョンが利用可能な場合、自動更新
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // 既に更新確認済みかチェック（無限ループ防止）
              const lastUpdatePrompt = sessionStorage.getItem('sw-update-prompted');
              const now = Date.now();
              if (lastUpdatePrompt && now - parseInt(lastUpdatePrompt) < 60000) {
                // 60秒以内に既に確認済み
                return;
              }
              sessionStorage.setItem('sw-update-prompted', now.toString());

              // 新しいバージョンが準備完了
              if (confirm('新しいバージョンが利用可能です。更新しますか？')) {
                // 新しいService Workerをアクティブにしてからリロード
                newWorker.postMessage({ type: 'SKIP_WAITING' });
                window.location.reload();
              }
            }
          });
        }
      });
    } catch (err) {
      console.warn('⚠️ [SW] Service Worker処理エラー:', err);
    }
  });
}

// =============================
// Capacitor ネイティブアプリ初期化
// =============================
if (Capacitor.isNativePlatform()) {
  // ステータスバー初期化（GIFTERRAテーマカラー適用）
  initGifterraStatusBar().catch(err => {
    console.warn('StatusBar initialization failed:', err);
  });

  // キーボード初期化（リサイズモード、スクロールアシスト）
  initGifterraKeyboard().catch(err => {
    console.warn('Keyboard initialization failed:', err);
  });
}

// =============================
// グローバルトランザクションリカバリシステム初期化
// モバイルMetaMaskでトランザクション承認時にタブがサスペンドされた場合の自動リカバリ
// =============================
import { initializeGlobalTransactionRecovery, registerRecoveryCallback } from './lib/globalTransactionRecovery';

// リカバリシステムを初期化
initializeGlobalTransactionRecovery();

// NFTミント成功時のリカバリコールバック（PWA/ブラウザ両対応）
const handleMintRecovery = async (_pendingTx: any) => {
  // PWA/ブラウザ判定
  const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                (window.navigator as any).standalone === true;

  // 少し待ってからリロード（ユーザーが状態を確認できるように）
  setTimeout(() => {
    if (isPWA) {
      window.location.replace(window.location.href);
    } else {
      window.location.href = window.location.href;
    }
  }, 1000);
};

// 各トランザクションタイプにコールバックを登録
registerRecoveryCallback('free_mint', handleMintRecovery);
registerRecoveryCallback('mint_modal', handleMintRecovery);
registerRecoveryCallback('stamp_reward', handleMintRecovery);

// TIP送金のリカバリコールバック（ガスレスと統一）
registerRecoveryCallback('tip', async (_pendingTx: any) => {
  setTimeout(() => {
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                  (window.navigator as any).standalone === true;
    if (isPWA) {
      window.location.replace(window.location.href);
    } else {
      window.location.href = window.location.href;
    }
  }, 1000);
});

// =============================
// グローバルエラーハンドラー（本番環境用 - デバッグUI無効化）
// =============================

// QRスキャナーのstopエラーを抑制（ユーザーに影響なし）
window.onerror = (message, source, lineno, colno, error) => {
  // QRスキャナーのstopエラーは無視
  if (typeof message === 'string' && message.includes('Cannot stop, scanner is not running')) {
    return true; // エラーを抑制
  }

  // その他のエラーはコンソールに記録しSentryへ送信
  console.error('エラー:', { message, source, lineno, colno, error });
  if (error instanceof Error) {
    Sentry.captureException(error);
  }
  return false;
};

// Polygon Mainnet configuration for ThirdwebProvider
// 複数のRPCエンドポイントでフォールバック対応（Android/モバイル接続の安定性向上）
const polygonChain = {
  chainId: 137,
  name: "Polygon",
  chain: "MATIC",
  rpc: [
    "https://polygon-rpc.com", // 公式RPC (CORS対応)
    "https://polygon-bor-rpc.publicnode.com", // PublicNode (無料・高速・CORS対応)
    // "https://rpc-mainnet.matic.network", // Polygon公式 (CORS非対応のためコメントアウト)
    "https://rpc-mainnet.maticvigil.com", // MaticVigil
    "https://1rpc.io/matic", // 1RPC (プライバシー保護)
  ],
  nativeCurrency: {
    name: "MATIC",
    symbol: "MATIC",
    decimals: 18,
  },
  shortName: "matic",
  slug: "polygon",
  testnet: false,
  explorers: [
    {
      name: "PolygonScan",
      url: "https://polygonscan.com",
      standard: "EIP3091",
    },
  ],
};

// Feature flags
const ENABLE_LEGACY_UI = import.meta.env.VITE_ENABLE_LEGACY_UI !== 'false';

// QueryClient for React Query (needed by ThirdwebProvider)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// URL判定
const path = location.pathname;
const uiParam = new URLSearchParams(location.search).get("ui");

const wantsReceive = path.includes("/receive");
const wantsReward = path.includes("/reward") || uiParam === "reward";
const wantsVending = path.includes("/vending") || path.includes("/content") || uiParam === "vending" || uiParam === "content";
const wantsSuperAdmin = path.includes("/super-admin") || path.includes("/superadmin") || uiParam === "super-admin" || uiParam === "superadmin";
// /admin/:tenantAddress または /admin/:tenantAddress/:page のパターンをチェック
// 例: /admin/0x1234...abcd または /admin/0x1234...abcd/settings
const adminMatch = path.match(/^\/admin(?:\/0x[a-fA-F0-9]{40}(?:\/[a-z-]+)?)?$/);
const wantsAdmin = adminMatch || (path.includes("/admin") && !path.includes("/super-admin") && !path.includes("/superadmin")) || uiParam === "admin";
const wantsTerminal = path.includes("/terminal") || uiParam === "terminal";
const wantsLegacy = path.includes("/legacy");
const wantsLogin = path.includes("/login") || uiParam === "login";
const wantsMypage = path.includes("/mypage") || uiParam === "mypage";
const wantsTerms = path.includes("/terms");
const wantsPrivacy = path.includes("/privacy");
const wantsProfile = path.includes("/profile") || uiParam === "profile";
const wantsUserProfile = path.includes("/user/") || uiParam === "user";
const wantsNotifications = path.includes("/notifications") || uiParam === "notifications";
const wantsRelayPay = path.includes("/relay-pay") || uiParam === "relay-pay";
const wantsTenantIntroduction = path.includes("/tenant/introduction") || uiParam === "tenant-introduction";
const wantsPlanSelection = path.includes("/tenant/plan-selection") || uiParam === "plan-selection";
const wantsTenantPaymentSuccess = path.includes("/tenant/payment-success") || uiParam === "tenant-payment-success";
const wantsTenantPayment = (path.includes("/tenant/payment") && !path.includes("/tenant/payment-success")) || uiParam === "tenant-payment";
const wantsTenantApplication = path.includes("/tenant/application") || uiParam === "tenant-application";
const wantsTipOverlay = path.match(/^\/overlay\/[a-f0-9-]{36}$/i);
const wantsTipPage = path.match(/^\/tip\/0x[a-fA-F0-9]{40}$/i); // TIPリンクページ（ゲストアクセス可能）
const wantsStampCheckin = path.includes("/stamp-checkin") || uiParam === "stamp-checkin";
const wantsCheckin = (path.includes("/checkin") && !path.includes("/stamp-checkin")) || uiParam === "checkin";
const wantsStampRallyBook = path.includes("/stamp-rally-book") || uiParam === "stamp-rally-book";
const wantsFreeMint = path.includes("/free-mint/") || uiParam === "free-mint";
const wantsEffectTest = path.includes("/effect-test") || uiParam === "effect-test";
const wantsPurchases = path.includes("/purchases") || uiParam === "purchases";
const wantsCommunities = path === "/communities" || path.startsWith("/communities/");
const wantsCommunityCreate = path === "/communities/create";
const wantsCommunityInvite = path.match(/^\/communities\/invite\/[A-Za-z0-9]+$/);
const wantsCommunitySettings = path.match(/^\/communities\/[0-9a-f-]{36}\/settings$/i);
const wantsCommunityDetail = path.match(/^\/communities\/[0-9a-f-]{36}$/i);

// =============================
// デバイス判別（モバイル vs タブレット/デスクトップ）
// =============================
const isMobileDevice = window.innerWidth < 768 || /iPhone|iPod|Android/i.test(navigator.userAgent);

// =============================
// ReactDOM ルート作成
// =============================
const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

// =============================
// 最小限のアプリ出力（MVP Phase 1）
// =============================

// 利用規約ページ（認証不要）
if (wantsTerms) {
  root.render(
    <React.StrictMode>
      <Suspense fallback={<LoadingFallback />}>
        <TermsOfServicePage />
      </Suspense>
    </React.StrictMode>
  );
} else if (wantsPrivacy) {
  // プライバシーポリシーページ（認証不要）
  root.render(
    <React.StrictMode>
      <Suspense fallback={<LoadingFallback />}>
        <PrivacyPolicyPage />
      </Suspense>
    </React.StrictMode>
  );
} else if (wantsTipOverlay) {
  // OBS用投げ銭エフェクトオーバーレイ（認証不要、透過背景）
  root.render(
    <React.StrictMode>
      <Suspense fallback={<div style={{ background: 'transparent' }} />}>
        <TipOverlayPage />
      </Suspense>
    </React.StrictMode>
  );
} else if (wantsTipPage) {
  // TIPリンクページ（ゲストアクセス可能、Privy + Thirdweb必須）
  root.render(
    <React.StrictMode>
      <Suspense fallback={<LoadingFallback />}>
        <QueryClientProvider client={queryClient}>
          <ThirdwebProvider
            activeChain={polygonChain}
            clientId={import.meta.env.VITE_THIRDWEB_CLIENT_ID}
          >
            <PrivyProvider
              appId={import.meta.env.VITE_PRIVY_APP_ID || ""}
              config={buildPrivyConfig({ loginMethods: ["wallet"] })}
            >
              <TipPage />
            </PrivyProvider>
          </ThirdwebProvider>
        </QueryClientProvider>
      </Suspense>
    </React.StrictMode>
  );
} else if (wantsEffectTest) {
  // エフェクトテストページ（開発用、認証不要）
  root.render(
    <React.StrictMode>
      <Suspense fallback={<LoadingFallback />}>
        <EffectTestPage />
      </Suspense>
    </React.StrictMode>
  );
} else if (wantsFreeMint) {
  // フリーミントページ（Privy + Thirdweb必須）
  root.render(
    <React.StrictMode>
      <Suspense fallback={<LoadingFallback />}>
        <QueryClientProvider client={queryClient}>
          <ThirdwebProvider
            activeChain={polygonChain}
            clientId={import.meta.env.VITE_THIRDWEB_CLIENT_ID}
          >
            <PrivyProvider
              appId={import.meta.env.VITE_PRIVY_APP_ID || ""}
              config={buildPrivyConfig({
                loginMethods: ["email", "google", "twitter", "discord", "wallet"],
                embeddedWallets: {
                  createOnLogin: 'users-without-wallets',
                  noPromptOnSignature: false,
                },
                mfaMethods: ['sms', 'totp'],
              })}
            >
              <FreeMintPage />
            </PrivyProvider>
          </ThirdwebProvider>
        </QueryClientProvider>
      </Suspense>
    </React.StrictMode>
  );
} else if (wantsReceive) {
  // 受け取りページ（認証不要、Thirdweb必須）
  root.render(
    <React.StrictMode>
      <Suspense fallback={<LoadingFallback />}>
        <QueryClientProvider client={queryClient}>
          <ThirdwebProvider
            activeChain={polygonChain}
            clientId={import.meta.env.VITE_THIRDWEB_CLIENT_ID}
          >
            <ReceivePage />
          </ThirdwebProvider>
        </QueryClientProvider>
      </Suspense>
    </React.StrictMode>
  );
} else if (wantsSuperAdmin) {
// Super Admin用の独立したレンダリング（Privy + Thirdweb）
  root.render(
    <React.StrictMode>
      <Suspense fallback={<LoadingFallback />}>
        <QueryClientProvider client={queryClient}>
          <ThirdwebProvider
            activeChain={polygonChain}
            clientId={import.meta.env.VITE_THIRDWEB_CLIENT_ID}
          >
            <PrivyProvider
              appId={import.meta.env.VITE_PRIVY_APP_ID || ""}
              config={buildPrivyConfig({
                loginMethods: ["email", "google", "twitter", "discord", "wallet"],
                embeddedWallets: {
                  createOnLogin: 'users-without-wallets',
                  noPromptOnSignature: false,
                },
                mfaMethods: ['sms', 'totp'],
              })}
            >
              <SuperAdminPage />
            </PrivyProvider>
          </ThirdwebProvider>
        </QueryClientProvider>
      </Suspense>
    </React.StrictMode>
  );
} else if (wantsTerminal) {
  // 受取ターミナルUI（デバイス判別：モバイル/タブレット）
  root.render(
    <React.StrictMode>
      <Suspense fallback={<LoadingFallback />}>
        <QueryClientProvider client={queryClient}>
          <ThirdwebProvider
            activeChain={polygonChain}
            clientId={import.meta.env.VITE_THIRDWEB_CLIENT_ID}
          >
            <PrivyProvider
              appId={import.meta.env.VITE_PRIVY_APP_ID || ""}
              config={buildPrivyConfig({
                loginMethods: ["email", "google", "twitter", "discord", "wallet"],
                embeddedWallets: {
                  createOnLogin: 'users-without-wallets',
                  noPromptOnSignature: false,
                },
                mfaMethods: ['sms', 'totp'],
              })}
            >
              {isMobileDevice ? <PaymentTerminalMobile /> : <PaymentTerminal />}
            </PrivyProvider>
          </ThirdwebProvider>
        </QueryClientProvider>
      </Suspense>
    </React.StrictMode>
  );
} else if (wantsAdmin) {
  // Admin用の独立したレンダリング（Privy + Thirdweb）
  root.render(
    <React.StrictMode>
      <Suspense fallback={<LoadingFallback />}>
        <QueryClientProvider client={queryClient}>
          <ThirdwebProvider
            activeChain={polygonChain}
            clientId={import.meta.env.VITE_THIRDWEB_CLIENT_ID}
          >
            <PrivyProvider
              appId={import.meta.env.VITE_PRIVY_APP_ID || ""}
              config={buildPrivyConfig({
                loginMethods: ["email", "google", "twitter", "discord", "wallet"],
                embeddedWallets: {
                  createOnLogin: 'users-without-wallets',
                  noPromptOnSignature: false,
                },
                mfaMethods: ['sms', 'totp'],
              })}
            >
              <TenantProvider>
                <AdminDashboard />
              </TenantProvider>
            </PrivyProvider>
          </ThirdwebProvider>
        </QueryClientProvider>
      </Suspense>
    </React.StrictMode>
  );
} else {
  // その他のページはPrivy + Thirdweb経由
  root.render(
    <React.StrictMode>
      <Suspense fallback={<LoadingFallback />}>
        <QueryClientProvider client={queryClient}>
          <ThirdwebProvider
            activeChain={polygonChain}
            clientId={import.meta.env.VITE_THIRDWEB_CLIENT_ID}
          >
            <PrivyProvider
              appId={import.meta.env.VITE_PRIVY_APP_ID || ""}
              config={buildPrivyConfig({
                loginMethods: ["email", "google", "twitter", "discord", "wallet"],
                embeddedWallets: {
                  createOnLogin: 'users-without-wallets',
                  noPromptOnSignature: false,
                },
                mfaMethods: ['sms', 'totp'],
              })}
            >
              <ErrorBoundary fallbackMessage="アプリケーションでエラーが発生しました">
              <AppWrapper>
              {wantsLogin ? (
                <LoginPage />
              ) : wantsMypage ? (
                <MypagePage />
              ) : wantsProfile ? (
                <ProfilePage />
              ) : wantsUserProfile ? (
                <UserProfile />
              ) : wantsNotifications ? (
                <NotificationsPage />
              ) : wantsRelayPay ? (
                <RelayPayPage />
              ) : wantsTenantIntroduction ? (
                <TenantIntroduction />
              ) : wantsPlanSelection ? (
                <PlanSelection />
              ) : wantsTenantPaymentSuccess ? (
                <TenantPaymentSuccess isMobile={isMobileDevice} />
              ) : wantsTenantPayment ? (
                <TenantPayment />
              ) : wantsTenantApplication ? (
                <TenantApplication />
              ) : wantsStampCheckin ? (
                <StampCheckinPage />
              ) : wantsCheckin ? (
                <CheckinPage />
              ) : wantsStampRallyBook ? (
                <StampRallyBookPage />
              ) : wantsPurchases ? (
                <MyPurchasesPage />
              ) : wantsCommunityCreate ? (
                <CommunityCreatePage />
              ) : wantsCommunityInvite ? (
                <CommunityInvitePage inviteCode={path.split('/').pop() || ''} />
              ) : wantsCommunitySettings ? (
                <CommunitySettingsPage communityId={path.split('/')[2] || ''} />
              ) : wantsCommunityDetail ? (
                <CommunityDetailPage communityId={path.split('/').pop() || ''} />
              ) : wantsCommunities ? (
                <CommunityListPage />
              ) : wantsReward ? (
                <RewardApp />
              ) : wantsVending ? (
                <VendingApp />
              ) : wantsLegacy && ENABLE_LEGACY_UI ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <h1 style={{ fontSize: '3rem', marginBottom: '1rem' }}>📜 Legacy UI</h1>
              <p style={{ fontSize: '1.2rem', opacity: 0.9 }}>
                旧UIは読み取り専用モードで保存されています
              </p>
              <div style={{
                marginTop: '2rem',
                padding: '1rem',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '8px',
                fontSize: '0.85rem'
              }}>
                <p>
                  <a
                    href="/"
                    style={{
                      color: 'white',
                      textDecoration: 'underline',
                      opacity: 0.9
                    }}
                  >
                    マイページに戻る →
                  </a>
                </p>
              </div>
            </div>
          </div>
              ) : (
                <LoginPage />
              )}
            </AppWrapper>
              </ErrorBoundary>
          </PrivyProvider>
        </ThirdwebProvider>
      </QueryClientProvider>
      </Suspense>
    </React.StrictMode>
  );
}